import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import AcceptanceRevocationPanel from '../Components/AcceptanceRevocationPanel';
import CustomerConfirmationLayout from '../Components/CustomerConfirmationLayout';
import { formatOrgContactLine } from '../Shared/customerContact';
import { formatLocalDate, formatLocalDateTime } from '../Shared/datetime';
import { fetchBidResponseContext } from '../Shared/publicApi';

const STATUS_CHIP_COLOR = {
	pending: 'warning',
	approved: 'success',
	pending_deposit: 'secondary',
	deposit_paid: 'success',
	pending_payment: 'info',
	paid: 'success',
	rejected: 'error',
	expired: 'warning',
	withdrawn: 'default',
};

const BidStatus = () => {
	const [params] = useSearchParams();
	const bidId = (params.get('bid_id') || '').trim();
	const token = (params.get('token') || '').trim();
	const [context, setContext] = useState(null);
	const [loading, setLoading] = useState(Boolean(bidId));
	const [loadError, setLoadError] = useState('');

	const refreshContext = useCallback(async () => {
		if (!bidId) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setLoadError('');
		try {
			const result = await fetchBidResponseContext(bidId, token);
			setContext(result);
		} catch {
			setLoadError('This proposal could not be found.');
			setContext(null);
		} finally {
			setLoading(false);
		}
	}, [bidId, token]);

	useEffect(() => {
		refreshContext();
	}, [refreshContext]);

	const contactLine = useMemo(
		() =>
			context
				? formatOrgContactLine({
						orgName: context.org_name,
						orgPhone: context.org_phone,
						orgEmail: context.org_email,
					})
				: null,
		[context],
	);

	const expirationLabel = useMemo(
		() => formatLocalDate(context?.expires_at),
		[context?.expires_at],
	);

	const rejectedAtLabel = useMemo(
		() => formatLocalDateTime(context?.rejected_at),
		[context?.rejected_at],
	);

	if (!bidId) {
		return (
			<CustomerConfirmationLayout
				headline="Invalid link"
				icon={
					<InfoOutlinedIcon
						sx={{ fontSize: 56, color: 'error.main' }}
						aria-hidden
					/>
				}
			>
				<Typography color="text.secondary">
					This link is missing a proposal reference.
				</Typography>
			</CustomerConfirmationLayout>
		);
	}

	if (loading) {
		return (
			<CustomerConfirmationLayout headline="Loading proposal status...">
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={32} />
				</Box>
			</CustomerConfirmationLayout>
		);
	}

	if (loadError || !context) {
		return (
			<CustomerConfirmationLayout
				headline="Proposal not found"
				icon={
					<InfoOutlinedIcon
						sx={{ fontSize: 56, color: 'error.main' }}
						aria-hidden
					/>
				}
			>
				<Typography color="text.secondary">{loadError}</Typography>
			</CustomerConfirmationLayout>
		);
	}

	const statusColor = STATUS_CHIP_COLOR[context.bid_status] || 'default';

	return (
		<CustomerConfirmationLayout
			orgLogoUrl={context.org_logo_url}
			orgName={context.org_name}
			headline="Proposal status"
			icon={
				<InfoOutlinedIcon
					sx={{ fontSize: 56, color: 'info.main' }}
					aria-hidden
				/>
			}
		>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ mb: 2, lineHeight: 1.7 }}
			>
				This page always shows the current state of your proposal. You can
				bookmark it - your original email will not update.
			</Typography>

			<Box
				sx={{
					textAlign: 'left',
					p: 2,
					borderRadius: 2,
					borderLeft: '4px solid',
					borderColor: 'info.main',
					bgcolor: 'action.hover',
					mb: 2,
				}}
			>
				<Typography variant="body2" sx={{ mb: 0.5 }}>
					<strong>Job:</strong> {context.job_title}
				</Typography>
				<Typography variant="body2" sx={{ mb: 1 }}>
					<strong>Amount:</strong>{' '}
					<Box component="span" sx={{ color: 'info.main', fontWeight: 700 }}>
						${context.amount}
					</Box>
				</Typography>
				{context.sent_to_email && (
					<Typography variant="caption" color="text.secondary" display="block">
						Proposal sent to {context.sent_to_email}
					</Typography>
				)}
				<Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography variant="body2" component="span">
						<strong>Status:</strong>
					</Typography>
					<Chip
						label={context.status_label || context.bid_status}
						color={statusColor}
						size="small"
						sx={{ fontWeight: 600 }}
					/>
				</Box>
				{expirationLabel && (
					<Typography
						variant="caption"
						color="text.secondary"
						display="block"
						sx={{ mt: 1 }}
					>
						Offer valid until {expirationLabel}
						{context.is_expired ? ' (expired)' : ''}
					</Typography>
				)}
				{rejectedAtLabel && (
					<Typography
						variant="caption"
						color="text.secondary"
						display="block"
						sx={{ mt: 0.5 }}
					>
						Declined {rejectedAtLabel}
					</Typography>
				)}
			</Box>

			<AcceptanceRevocationPanel
				variant="status"
				orgName={context.org_name}
				acceptedAt={context.accepted_at}
				acceptanceRevokedAt={context.acceptance_revoked_at}
				canRevoke={context.can_revoke}
				acceptanceFinalized={context.acceptance_finalized}
				revocationDeadline={context.revocation_deadline}
				token={token}
				bidId={bidId}
				onRevoked={refreshContext}
			/>

			{contactLine && (
				<Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
					<Typography variant="body2">{contactLine}</Typography>
				</Alert>
			)}
		</CustomerConfirmationLayout>
	);
};

export default BidStatus;
